import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Typography,
} from '@mui/material';
import {
  NoDataContainer,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from '../../Common/styles/table';
import DataTableLayout from '../../Common/UI/DataTableLayout';
import DataTableFooter from '../../Common/UI/DataTableFooter';
import { dataTableListShell } from '../../../utils/dataTableShell';
import http from '../../../utils/http';
import { errorToastMessage } from '../../../utils/toast';
import { DateTime } from 'luxon';
import HardDeleteConfirmModal from '../Modals/HardDeleteConfirmModal';

// Phase 6c — extended with tier + studyId/studyName so a single eligible list
// shows both registry-tier and study-tier rows.
interface EligibleParticipant {
  participantId: string;
  name: string;
  email: string;
  redactedAt: string;
  gracePeriodEnds: string;
  tier: 'registry' | 'study';
  studyId: string | null;
  studyName: string | null;
}

interface DeleteTarget {
  participantId: string;
  studyId: string | null;
}

const HardDeleteEligibleList: React.FC = () => {
  const [data, setData] = useState<EligibleParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState<DeleteTarget | null>(null);

  const fetchEligible = async () => {
    try {
      setLoading(true);
      const res = await http.get('/admin/participants/hard-delete/eligible');
      setData(res.data?.data ?? []);
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEligible();
  }, []);

  const handleDeleted = () => {
    setData((prev) =>
      prev.filter(
        (p) =>
          !(
            p.participantId === target?.participantId &&
            (p.studyId ?? null) === (target?.studyId ?? null)
          ),
      ),
    );
    setTarget(null);
  };

  return (
    <Box
      sx={{
        ...dataTableListShell,
        p: 2,
        backgroundColor: "#fff",
        borderRadius: "10px",
      }}
    >
      <DataTableLayout
        toolbar={
          <Typography variant="h6" color="text.primary" sx={{ mb: 2 }}>
            Participants Eligible for Permanent Deletion
          </Typography>
        }
        footer={
          <DataTableFooter
            filteredCount={data.length}
            page={0}
            pageSize={data.length || 1}
            onPageChange={() => {}}
            loading={loading}
            hidePagination
          />
        }
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : data.length === 0 ? (
          <NoDataContainer>
            <Typography>No participants are currently eligible for permanent deletion.</Typography>
          </NoDataContainer>
        ) : (
          <Table
            sx={TableBorderRadiusTopLeftRight}
            stickyHeader
            className="scrollable-table"
          >
            <TableHead>
              <TableRow>
                <StyledTableCell>Tier</StyledTableCell>
                <StyledTableCell>Participant ID</StyledTableCell>
                <StyledTableCell>Study</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Email</StyledTableCell>
                <StyledTableCell>Redacted On</StyledTableCell>
                <StyledTableCell>Eligible Since</StyledTableCell>
                <StyledTableCell>Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row) => (
                <TableRow key={`${row.participantId}:${row.studyId ?? 'registry'}`}>
                  <TableCell>
                    <Chip
                      size="small"
                      color={row.tier === 'registry' ? 'error' : 'warning'}
                      label={row.tier === 'registry' ? 'Database' : 'Study'}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {row.participantId}
                  </TableCell>
                  <TableCell>
                    {row.tier === 'study'
                      ? (row.studyName ?? row.studyId ?? '-')
                      : '—'}
                  </TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>
                    {DateTime.fromISO(row.redactedAt).toFormat('MM/dd/yyyy')}
                  </TableCell>
                  <TableCell>
                    {DateTime.fromISO(row.gracePeriodEnds).toFormat('MM/dd/yyyy')}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() =>
                        setTarget({
                          participantId: row.participantId,
                          studyId: row.tier === 'study' ? row.studyId : null,
                        })
                      }
                    >
                      Purge Permanently
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DataTableLayout>

      {target && (
        <HardDeleteConfirmModal
          open={!!target}
          participantId={target.participantId}
          studyId={target.studyId ?? undefined}
          onClose={() => setTarget(null)}
          onDeleted={handleDeleted}
        />
      )}
    </Box>
  );
};

export default HardDeleteEligibleList;
